import type { Board } from "../board";
import { getMobGoldValue } from "../economy/mobGoldValues";
import { DamageType } from "../damageType";
import { ALL_DIRECTIONS, Direction, cycleDirection, directionDelta, directionFromVector } from "../direction";
import { Hit } from "../effects/hit";
import { MovementEffect } from "../effects/movementEffect";
import type { PostDeserializable, SomethingWithState } from "../interfaces";
import type { PhysicalThing } from "../physicalThing";
import type { PhysicalThingWithHp } from "../physicalThingWithHp";
import { STD_BASE_HP } from "../physicalThingWithHp";
import { randomChance, randomElement, randomInt } from "../random";
import { deserializeXY } from "../serialization";
import type { Thing } from "../thing";
import { Sound } from "../things/sounds/sound";
import { SoundId } from "../things/sounds/soundId";
import { Mob } from "../mob";
import { ParticleType } from "../particleType";
import { XY } from "../xy";
import { ZOrder } from "../zOrder";
import { BigSquidCorpse } from "./bigSquidCorpse";
import { BigSquidTentacle, BigSquidTentacleState } from "./bigSquidTentacle";
import { BigSquidTentacleCut } from "./bigSquidTentacleCut";
import type { SimpleBoard } from "../levelgenerator/simpleBoard.ts";
import { MultiTileThingController } from "../things/multitile/multiTileThingController.ts";
import { MultiTileThingPart } from "../things/multitile/multiTileThingPart.ts";

export enum BigSquidState {
	NORMAL = "NORMAL",
	HURT = "HURT",
}

const ENERGY_NEEDED_TO_MOVE = 3;

export class BigSquid extends Mob implements PostDeserializable, SomethingWithState {
	static key = "BigSquid";

	override getBaseGoldValue(): number {
		return getMobGoldValue(BigSquid.key);
	}

	private tentacles: Set<PhysicalThing> = new Set();
	private squidState: BigSquidState = BigSquidState.NORMAL;
	remoteControlDirection: Direction | null = null;
	private energy: number = 3;
	private probabilityToMoveWhenIdle: number = 0.05;
	private alwaysKnowsWhereTargetIs: boolean = false;
	private aggroDistanceReachable: number = 45;
	private aggroDistanceRadius: number = 7;
	private noiseCoolDown: number = 10;
	private noiseProbability: number = 0.05;
	private minimumDistanceToNoise: number = 30;
	private lastNoiseTurn: number | null = null;

	constructor(xy: XY) {
		super(xy);
		this.resistsPermanentFreeze = true;
		this.damageMultipliers.set(DamageType.BUBBLE, 0);
		this.damageMultipliers.set(DamageType.FIRE, 0.4);
		this.maxHp = STD_BASE_HP * 3;
		this.hp = this.maxHp;
	}

	getState(): BigSquidState {
		return this.squidState;
	}

	override getBloodParticleType(): ParticleType {
		return ParticleType.BLOOD;
	}

	override getZOrder(): number {
		return ZOrder.MOB + 1 + 100;
	}

	override applyDamage(type: DamageType | null, amount: number, source: Thing | null): number {
		const ret = super.applyDamage(type, amount, source);
		if (this.squidState !== BigSquidState.HURT && this.hp < this.maxHp) {
			this.squidState = BigSquidState.HURT;
		}
		return ret;
	}

	processMobAction(_board: Board): void {
		this.replacesAllDeadTentacles();

		// Save previous positions of all tentacles for movement effects
		const previousPositions = new Map<PhysicalThing, XY>();
		for (const t of this.tentacles) {
			previousPositions.set(t, t.xy);
		}

		const target = this.board!.player;
		if (!target || !target.active) return;

		if (!this.hasTarget(target)) {
			this.idle();
		} else {
			if (this.canAttack(target)) {
				this.runAttack(target);
			} else {
				if (!this.knowsWhereTargetIs(target)) {
					this.idle();
				} else {
					const tentaclesAlive = [...this.tentacles].filter((t) => t.active && t instanceof BigSquidTentacle).length;

					this.energy += tentaclesAlive;
					this.energy = Math.min(this.energy, ENERGY_NEEDED_TO_MOVE);

					if (this.energy >= ENERGY_NEEDED_TO_MOVE) {
						this.energy -= ENERGY_NEEDED_TO_MOVE;
						if (!this.moveTowardsTarget(target)) {
							this.moveTowardsTargetHeuristically(target);
						}
					}
				}
			}
		}

		if (this.active) this.rotateTentacles();

		// Add movement effects for tentacles that moved
		for (const t of this.tentacles) {
			const previousXY = previousPositions.get(t);
			if (!previousXY) continue;
			if (previousXY.equals(t.xy)) continue;
			this.board!.addEffect(new MovementEffect(t, previousXY, t.xy));
		}

		this.checkTentaclesState();
		this.replacesAllDeadTentacles();
		this.processIdleSound();
	}

	private processIdleSound(): void {
		const target = this.board!.player;
		if (!target || !target.active) return;
		const distance = this.xy.distanceTo(target.xy);
		if (
			distance <= this.minimumDistanceToNoise &&
			randomChance(this.noiseProbability) &&
			(this.lastNoiseTurn === null || this.board!.turnCount - this.lastNoiseTurn > this.noiseCoolDown)
		) {
			this.board!.addNonPhysicalThing(new Sound(SoundId.BIG_SQUID_SOUND, this.xy));
			this.lastNoiseTurn = this.board!.turnCount;
		}
	}

	private hasTarget(target: PhysicalThingWithHp): boolean {
		return target != null && target.active;
	}

	private checkTentaclesState(): void {
		for (const tentacle of this.tentacles) {
			if (!(tentacle instanceof BigSquidTentacle)) continue;
			const newState = this.board!.isPassable(tentacle.xy)
				? BigSquidTentacleState.EXTENDED
				: BigSquidTentacleState.RETRACTED;
			tentacle.setState(newState);
		}
	}

	private runAttack(target: PhysicalThingWithHp): void {
		if (!target || !target.active) return;

		// Tentacles attack
		for (const t of this.getTentaclesCopy()) {
			if (!t.active || !(t instanceof BigSquidTentacle)) continue;
			if (t.getState() !== BigSquidTentacleState.EXTENDED) continue;
			if (!target.xy.isInSquareRadius(t.xy, 1)) continue;
			this.attackTarget(target);
		}

		// Head attack
		if (target.xy.isInSquareRadius(this.xy, 1)) {
			this.attackTarget(target);
		}
	}

	private attackTarget(target: PhysicalThingWithHp): void {
		if (!target.active) return;
		this.board!.addEffect(new Hit(target.xy));
		target.applyDamage(DamageType.MELEE, STD_BASE_HP, this);
		this.board!.addNonPhysicalThing(new Sound(SoundId.MOB_PHISICAL_ATTACK, target.xy));
	}

	private moveTowardsTarget(target: PhysicalThingWithHp): boolean {
		if (target.xy.isInSquareRadius(this.xy, 1)) return false;

		const deltax = target.xy.x - this.xy.x;
		const deltay = target.xy.y - this.xy.y;
		const vector = new XY(Math.sign(deltax), Math.sign(deltay));

		let direction = directionFromVector(vector);
		if (!direction) return false;

		// Less random when close to player
		if (!this.xy.isInSquareRadius(target.xy, 5)) {
			direction = cycleDirection(direction, randomInt(-1, 1));
		}

		const proposedXY = this.xy.add(directionDelta(direction));
		if (!this.canMoveTo(proposedXY)) return false;

		return this.moveInDirection(direction);
	}

	private moveTowardsTargetHeuristically(target: PhysicalThingWithHp): boolean {
		let best: XY | null = null;
		const targetXY = target.xy;

		for (const neighbor of this.board!.getNeighbors(this.xy)) {
			if (!this.canMoveTo(neighbor)) continue;
			if (!best || neighbor.distanceTo(targetXY) <= best.distanceTo(targetXY)) {
				best = neighbor;
			}
		}

		if (!best) return false;

		const deltax = best.x - this.xy.x;
		const deltay = best.y - this.xy.y;
		const vector = new XY(Math.sign(deltax), Math.sign(deltay));
		const direction = directionFromVector(vector);
		if (!direction) return false;

		return this.moveInDirection(direction);
	}

	override canMoveTo(target: XY): boolean {
		if (!this.board!.isPassable(target)) return false;
		// Don't move on top of another BigSquid
		if (this.board!.getThingsAt(target).some((t) => t instanceof BigSquid && t !== this)) return false;

		const direction = directionFromVector(target.subtract(this.xy));
		if (!direction) return false;

		const oneStepFurther = target.add(directionDelta(direction));
		if (!this.board!.isInBounds(oneStepFurther)) return false;

		return true;
	}

	private moveInDirection(d: Direction): boolean {
		const targetXY = this.xy.add(directionDelta(d));
		const oneStepFurther = targetXY.add(directionDelta(d));

		if (!this.board!.isPassable(targetXY) || !this.board!.isInBounds(oneStepFurther)) return false;

		this.moveHead(targetXY);

		// Move tentacles
		for (const tentacle of this.getTentaclesCopy()) {
			if (!tentacle.active) continue;
			const newXY = tentacle.xy.add(directionDelta(d));
			if (!this.board!.isInBounds(newXY)) continue;
			this.board!.moveThing(tentacle, newXY);
		}

		return true;
	}

	private rotateTentacles(): void {
		const hasLiveTentacle = [...this.tentacles].some((t) => t.active && t instanceof BigSquidTentacle);
		if (!hasLiveTentacle) return;

		const tentaclesMoved = new Set<PhysicalThing>();
		const tentaclesArray = [...this.tentacles].sort(() => randomInt(-1, 1));

		for (const tentacle of tentaclesArray) {
			if (!tentacle.active) continue;
			if (tentaclesMoved.has(tentacle)) continue;

			const currentDirection = this.getDirectionRelativeToHead(tentacle);
			if (!currentDirection) continue;

			const newDirection = cycleDirection(currentDirection, Math.random() < 0.5 ? 1 : -1);
			const tentacleToSwitch = this.getTentacleByDirection(newDirection);
			if (!tentacleToSwitch || !tentacleToSwitch.active) continue;
			if (tentaclesMoved.has(tentacleToSwitch)) continue;

			this.moveTentacleAroundHead(tentacle, newDirection);
			this.moveTentacleAroundHead(tentacleToSwitch, currentDirection);

			tentaclesMoved.add(tentacle);
			tentaclesMoved.add(tentacleToSwitch);
		}
	}

	private moveTentacleAroundHead(tentacle: PhysicalThing, newDirection: Direction): void {
		if (tentacle instanceof BigSquidTentacle) {
			tentacle.setDirection(newDirection);
		} else if (tentacle instanceof BigSquidTentacleCut) {
			tentacle.setDirection(newDirection);
		}
		const newXY = this.xy.add(directionDelta(newDirection));
		this.board!.moveThing(tentacle, newXY);
	}

	private getTentacleByDirection(direction: Direction): PhysicalThing | null {
		const target = this.xy.add(directionDelta(direction));
		for (const t of this.tentacles) {
			if (t.xy.equals(target)) return t;
		}
		return null;
	}

	private getDirectionRelativeToHead(thing: PhysicalThing): Direction | null {
		return directionFromVector(thing.xy.subtract(this.xy));
	}

	private replaceDeadTentacle(tentacle: BigSquidTentacle): BigSquidTentacleCut {
		const cut = new BigSquidTentacleCut(this.xy.add(directionDelta(tentacle.getDirection()!)));
		cut.setDirection(tentacle.getDirection()!);
		this.board!.addThing(cut);
		return cut;
	}

	private replacesAllDeadTentacles(): void {
		if (!this.active) return;
		const cuts: BigSquidTentacleCut[] = [];
		const dead: PhysicalThing[] = [];

		for (const t of this.tentacles) {
			if (!t.active && t instanceof BigSquidTentacle) {
				cuts.push(this.replaceDeadTentacle(t));
				dead.push(t);
			} else if (!t.active) {
				dead.push(t);
			}
		}

		for (const d of dead) this.tentacles.delete(d);
		for (const c of cuts) this.tentacles.add(c);
	}

	private moveHead(newXY: XY): void {
		const oldXY = this.xy;
		this.xy = newXY;

		if (!this.board!.isInBounds(newXY)) return;

		this.board!.addEffect(new MovementEffect(this, oldXY, newXY));

		const deltaX = newXY.x - oldXY.x;
		if (deltaX > 0) this.lookDirection = Direction.E;
		if (deltaX < 0) this.lookDirection = Direction.W;
	}

	addTentacle(tentacle: BigSquidTentacle): void {
		this.tentacles.add(tentacle);
		tentacle.setHead(this);
	}

	protected onZeroHp(source: Thing | null, type: DamageType | null): void {
		super.onZeroHp(source, type);
		for (const t of this.getTentaclesCopy()) {
			if (t.active && t instanceof BigSquidTentacle) {
				t.kill();
			} else {
				this.board!.removeAtEndOfTurn(t);
			}
		}
	}

	notifyKilledTentacle(killedTentacle: BigSquidTentacle): void {
		if (!this.active) return;
		const cut = this.replaceDeadTentacle(killedTentacle);
		this.tentacles.delete(killedTentacle);
		this.tentacles.add(cut);
	}

	generateCorpse(): PhysicalThing | null {
		return new BigSquidCorpse(this.xy);
	}

	override generateDeathSound(): Sound {
		return new Sound(SoundId.BIG_SQUID_DIE);
	}

	private getTentaclesCopy(): Set<PhysicalThing> {
		return new Set(this.tentacles);
	}

	override canAttack(target: PhysicalThingWithHp): boolean {
		if (!target) return false;

		// Check if head can attack
		if (this.xy.isInSquareRadius(target.xy, 1)) return true;

		// Check if any extended tentacle can attack
		for (const t of this.tentacles) {
			if (!t.active || !(t instanceof BigSquidTentacle)) continue;
			if (t.getState() !== BigSquidTentacleState.EXTENDED) continue;
			if (target.xy.isInSquareRadius(t.xy, 1)) return true;
		}

		return false;
	}

	private idle(): void {
		if (!randomChance(this.probabilityToMoveWhenIdle)) return;

		const candidates = this.board!.getNeighbors(this.xy).filter((xy) => this.canMoveTo(xy));
		const destination = randomElement(candidates);
		if (!destination) return;

		const deltax = destination.x - this.xy.x;
		const deltay = destination.y - this.xy.y;
		const vector = new XY(Math.sign(deltax), Math.sign(deltay));
		const direction = directionFromVector(vector);
		if (direction) this.moveInDirection(direction);
	}

	private knowsWhereTargetIs(target: PhysicalThingWithHp): boolean {
		if (this.alwaysKnowsWhereTargetIs) return true;
		if (target === this.board!.player) {
			const distanceMap = this.board!.getDistanceMapToPlayer();
			if (distanceMap) {
				const distance = distanceMap.get(this.xy);
				if (distance !== undefined && distance <= this.aggroDistanceReachable) return true;
			}
		}
		return target.xy.distanceTo(this.xy) <= this.aggroDistanceRadius;
	}

	postDeserialize(board: Board): void {
		// Re-link tentacles: find BigSquidTentacles adjacent to head
		for (const thing of board.getPhysicalThings()) {
			if (!(thing instanceof BigSquidTentacle)) continue;
			if (!thing.xy.isInSquareRadius(this.xy, 1) || thing.xy.equals(this.xy)) continue;
			// Check if the tentacle's serialized headXY matches our position
			this.addTentacle(thing);
		}
		// Also pick up existing cuts
		for (const thing of board.getPhysicalThings()) {
			if (!(thing instanceof BigSquidTentacleCut)) continue;
			if (!thing.xy.isInSquareRadius(this.xy, 1) || thing.xy.equals(this.xy)) continue;
			this.tentacles.add(thing);
		}
	}

	static create(board: Board, targetXY: XY): BigSquid | null {
		for (const xy of targetXY.getNeighbors()) {
			if (!board.isInBounds(xy)) return null;
		}
		const bigSquid = new BigSquid(targetXY);
		board.addThing(bigSquid);
		for (const direction of ALL_DIRECTIONS) {
			const tentacle = new BigSquidTentacle(targetXY.add(directionDelta(direction)));
			tentacle.setDirection(direction);
			board.addThing(tentacle);
			bigSquid.addTentacle(tentacle);
		}
		bigSquid.checkTentaclesState();
		return bigSquid;
	}

	// bigSquid = BigSquid.create(board, xy);
	// static initOnBoard(xy: XY, board: SimpleBoard, _props?: Record<string, unknown>): PhysicalThing[] {
	// 	BigSquid.create(board, xy);
	//
	// 	const core = new ArtilleryRobot();
	// 	core.xy = xy;
	//
	// 	const controller = new MultiTileThingController(core);
	// 	core.controller = controller;
	// 	board.putThing(core);
	//
	// 	const offsets = [new XY(0, 0), new XY(1, 0), new XY(0, 1), new XY(1, 1)];
	// 	const parts: MultiTileThingPart[] = [];
	// 	for (const offset of offsets) {
	// 		const part = new MultiTileThingPart(xy.add(offset));
	// 		part.linkToController(controller);
	// 		controller.getParts().add(part);
	// 		board.putThing(part);
	// 		parts.push(part);
	// 	}
	//
	// 	return [core, ...parts];
	// }

	serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			energy: this.energy,
			squidState: this.squidState,
		};
	}

	static deserialize(data: any): BigSquid {
		const s = new BigSquid(deserializeXY(data.xy));
		s.restoreMobFields(data);
		s.energy = data.energy ?? 3;
		s.squidState = data.squidState ?? BigSquidState.NORMAL;
		return s;
	}
}
